'use client';

import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Loader2, Pause, Play, RefreshCw, CheckCircle2 } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useActivationProgress } from '@/features/playbooks/api/use-command-center';
import {
  useActivation,
  useActivatePlaybook,
  useCompletePlaybook,
  usePausePlaybook,
  useRecalculateProgress,
} from '@/features/playbooks/api/use-playbook-activations';
import { AddObligationDialog } from '@/features/playbooks/components/AddObligationDialog';
import { PhaseAccordion } from '@/features/playbooks/components/PhaseAccordion';
import { PlaybookProgressBar } from '@/features/playbooks/components/PlaybookProgressBar';
import { PlaybookStatusBadge } from '@/features/playbooks/components/PlaybookStatusBadge';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/tasks/playbooks/$activationId/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workflowDetail'),
  }),
  component: PlaybookDetailPage,
});

function PlaybookDetailPage() {
  const { t } = useTranslation('tasks');
  const { activationId } = Route.useParams();
  const { activation, isLoading } = useActivation(activationId);
  const { progress, isLoading: progressLoading } = useActivationProgress(activationId);
  const { pause, isPausing } = usePausePlaybook();
  const { activate, isActivating } = useActivatePlaybook();
  const { complete, isCompleting } = useCompletePlaybook();
  const { recalculate, isRecalculating } = useRecalculateProgress();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!activation) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('workflowDetail.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WorkflowSectionTabs activeTab="runs" />
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks/playbooks">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{activation.name}</h1>
              <PlaybookStatusBadge status={activation.status} />
            </div>
            <p className="text-sm text-muted-foreground">
              {activation.templateName} &middot; {activation.reportingPeriod?.name ?? '—'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {activation.status !== 'completed' && (
            <AddObligationDialog activationId={activationId} />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void recalculate(activationId)}
            disabled={isRecalculating}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRecalculating ? 'animate-spin' : ''}`} />
            {t('workflowDetail.recalculate')}
          </Button>
          {activation.status === 'draft' && (
            <Button
              size="sm"
              onClick={() => void activate(activationId)}
              disabled={isActivating}
            >
              <Play className="mr-2 h-4 w-4" />
              {t('workflowDetail.activate')}
            </Button>
          )}
          {activation.status === 'active' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void pause(activationId)}
              disabled={isPausing}
            >
              <Pause className="mr-2 h-4 w-4" />
              {t('workflowDetail.pause')}
            </Button>
          )}
          {activation.status === 'paused' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void activate(activationId)}
              disabled={isActivating}
            >
              <Play className="mr-2 h-4 w-4" />
              {t('workflowDetail.resume')}
            </Button>
          )}
          {(activation.status === 'active' || activation.status === 'paused') && (
            <Button
              size="sm"
              onClick={() => void complete(activationId)}
              disabled={isCompleting}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {t('workflowDetail.complete')}
            </Button>
          )}
        </div>
      </div>

      {/* Progress summary */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border p-4">
          <p className="text-sm font-medium text-muted-foreground">{t('workflowDetail.overallProgress')}</p>
          <div className="mt-2">
            <PlaybookProgressBar
              completed={activation.completedObligations}
              total={activation.totalObligations}
            />
          </div>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-sm font-medium text-muted-foreground">{t('workflowDetail.obligations')}</p>
          <p className="mt-1 text-2xl font-bold">
            {String(activation.completedObligations)}/{String(activation.totalObligations)}
          </p>
        </div>
        <div className="rounded-lg border p-4">
          <p className="text-sm font-medium text-muted-foreground">{t('workflowDetail.tasks')}</p>
          <p className="mt-1 text-2xl font-bold">
            {String(activation.completedTasks)}/{String(activation.totalTasks)}
          </p>
        </div>
      </div>

      {/* Phases */}
      {progressLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : progress ? (
        <div className="space-y-4">
          {progress.phases.map((phase) => (
            <PhaseAccordion
              key={phase.phaseId}
              phase={phase}
              activationId={activationId}
              activationStatus={activation.status}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
